import { sanitizeMarkdown } from "@/utils/documentUtils";
import { Artefact, ArtefactType, ArtefactDetectionConfig } from "@/types/artefacts";

import { BaseArtefactDetector } from "./BaseDetector";
import { DETECTION_PATTERNS } from "./patterns";

/**
 * Detects structured documents (markdown-like content)
 */
export class DocumentDetector extends BaseArtefactDetector {
  detect(content: string, messageId: string, config: ArtefactDetectionConfig) {
    if (!this.validateContent(content)) {
      // Check if it's whitespace-only content (not empty)
      const isWhitespaceOnly = content && content.length > 0 && content.trim().length === 0;
      const skipCount = isWhitespaceOnly ? 1 : 0;
      const warnings = isWhitespaceOnly ? [] : ["Invalid content provided for document detection"];

      return this.createResult([], skipCount, warnings);
    }

    const artefacts: Artefact[] = [];
    const warnings: string[] = [];
    let skippedCount = 0;

    // Analyze content structure
    const structureScore = this.analyzeContentStructure(content);

    // Must have some structure and meet length requirement
    if (structureScore >= 2 && content.length >= config.minDocumentChars) {
      // Remove code blocks for document content estimation
      const contentWithoutCode = content.replace(DETECTION_PATTERNS.CODE_BLOCK, "");

      if (contentWithoutCode.trim().length >= config.minDocumentChars) {
        const title = this.generateDocumentTitle(content);
        const wordCount = this.estimateWordCount(contentWithoutCode);

        const artefact: Artefact = {
          id: this.generateId(messageId, "document", 0),
          type: ArtefactType.DOCUMENT,
          title,
          content: content.trim(),
          messageId,
          timestamp: this.getCurrentTimestamp(),
          metadata: this.createMetadata("structured_content", `~${wordCount} words`, {
            fileExtension: "md",
          }),
        };

        artefacts.push(artefact);
      } else {
        skippedCount++;
      }
    } else {
      // Only count skip if there's actual content to skip (not just whitespace)
      if (content.trim().length > 0) {
        skippedCount++;
      }
    }

    return this.createResult(artefacts, skippedCount, warnings);
  }

  /**
   * Analyze content structure and assign a score
   */
  private analyzeContentStructure(content: string): number {
    let score = 0;

    if (DETECTION_PATTERNS.HEADINGS.test(content)) score += 3;
    if (
      DETECTION_PATTERNS.UNORDERED_LISTS.test(content) ||
      DETECTION_PATTERNS.ORDERED_LISTS.test(content)
    )
      score += 2;
    if (
      DETECTION_PATTERNS.EMPHASIS_BOLD.test(content) ||
      DETECTION_PATTERNS.EMPHASIS_ITALIC.test(content)
    )
      score += 1;
    if (DETECTION_PATTERNS.LINKS.test(content)) score += 1;

    return score;
  }

  /**
   * Generate a title for the document based on content
   */
  private generateDocumentTitle(content: string): string {
    // Try to extract first heading
    const headingMatch = content.match(/^#{1,6}\s+(.+)$/m);

    if (headingMatch) {
      const title = sanitizeMarkdown(headingMatch[1].trim());

      if (title.length > 0) {
        return title.substring(0, 50);
      }
    }

    // Fallback to first line
    const firstLine = content.split("\n")[0].trim();

    if (firstLine.length > 0) {
      const cleanTitle = sanitizeMarkdown(firstLine);

      if (cleanTitle.length > 0) {
        return cleanTitle.substring(0, 50) + (cleanTitle.length > 50 ? "..." : "");
      }
    }

    return "Document";
  }

  /**
   * Estimate word count in content
   */
  private estimateWordCount(content: string): number {
    return content.trim().split(/\s+/).length;
  }
}
